<template>
  <button
    :disabled="disabled"
    :type="type"
    :class="['acv-button ripple', computedClasses]"
  >
    <template v-if="hasPrepend">
      <div class="acv-button__prepend">
        <slot name="prepend"> </slot>
      </div>
    </template>

    <AcvProgressIndicator
      v-if="loading"
      class="button-progress-indicator"
      v-bind="computedProgressIndicatorOptions"
    />
    <slot v-else />

    <template v-if="hasAppend">
      <div class="acv-button__append">
        <slot name="append"> </slot>
      </div>
    </template>
  </button>
</template>

<script>
import propValidator from '@/acv/utilities/propValidator';

export default {
  name: 'AcvButton',
  props: {
    /**
     * Object with options to customize the progress indicator.
     * Fields map directly to the props on
     * [AcvProgressIndicator.vue](https://github.com/acv-auctions/acv-web-vuejs/blob/master/src/shared/components/ProgressIndicator/ProgressIndicator.vue)
     */
    progressIndicatorOptions: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Type of button to be render
     */
    type: {
      type: String,
      default: 'button',
      validator(buttonType) {
        return propValidator(buttonType, ['button', 'submit']);
      },
    },
    /**
     * Button variant style
     */
    variant: {
      type: String,
      default: 'primary',
      validator(variant) {
        return propValidator(variant, [
          'primary',
          'secondary',
          'tertiary',
          'link',
          'destructive',
          'suspended',
          'notification',
          'error',
        ]);
      },
    },
    /**
     * Checks if the button should be disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Shows an indeterminate progress indicator.
     * Use the prop `progressIndicatorOptions` to customize the indicator.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Prepends an icon before the button text content
     */
    iconPrepend: {
      type: [String, Array],
      default: '',
    },
    /**
     * Appends an icon before the button content
     */
    iconAppend: {
      type: [String, Array],
      default: '',
    },
    /**
     * Shortens the button height
     */
    short: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedProgressIndicatorOptions() {
      const defaultProgressIndicatorOptions = {
        variant: ['secondary', 'link', 'suspended'].includes(this.variant)
          ? 'primary'
          : 'secondary',
        mode: 'circular',
        indeterminate: true,
        size: this.short ? 16 : 20,
        stroke: 2,
        width: 62,
      };

      return {
        ...defaultProgressIndicatorOptions,
        ...this.progressIndicatorOptions,
      };
    },
    computedClasses() {
      return {
        'acv-button--primary': this.variant === 'primary',
        'acv-button--secondary': this.variant === 'secondary',
        'acv-button--tertiary': this.variant === 'tertiary',
        'acv-button--link': this.variant === 'link',
        'acv-button--destructive': this.variant === 'destructive',
        'acv-button--suspended': this.variant === 'suspended',
        'acv-button--notification': this.variant === 'notification',
        'acv-button--short': this.short,
        'acv-button--error': this.variant === 'error',
      };
    },
    hasPrepend() {
      return this.$slots.prepend || this.iconPrepend;
    },
    hasAppend() {
      return this.$slots.append || this.iconAppend;
    },
  },
};
</script>

<style lang="scss" scoped>
.acv-button {
  @include button;
  @apply tw-relative tw-inline-flex tw-h-12 tw-cursor-pointer tw-select-none tw-items-center tw-justify-center tw-rounded tw-px-4 tw-py-3 tw-capitalize tw-outline-none;

  transition: all 0.15s ease-in-out;

  &__prepend {
    @apply tw-mr-2;
  }

  &__append {
    @apply tw-ml-2;
  }

  &__icon {
    @apply tw-h-3 tw-w-3;
  }

  &--short {
    @apply tw-h-8 tw-py-1;
  }

  &[disabled],
  &.disabled {
    cursor: not-allowed;
    opacity: 0.37;
  }

  &--primary {
    @apply tw-rounded-full tw-border tw-border-solid tw-border-transparent tw-bg-primary-base tw-bg-primary-gradient tw-text-white;

    &:hover {
      @apply tw-bg-primary-base;

      background-image: none;
      box-shadow:
        0 2px 4px rgb(0 0 0 / 14%),
        0 1px 5px rgb(0 0 0 / 20%);
    }

    &:active,
    &:focus,
    &:active:focus {
      @apply tw-bg-pink tw-outline-none;

      background-image: none;
    }
  }

  &--secondary {
    @apply tw-border tw-border-solid tw-border-pink tw-bg-transparent tw-text-pink;

    &:hover {
      @apply tw-bg-pink-80;
    }

    &:focus {
      @apply tw-bg-pink-120 tw-outline-none;
    }

    &:active,
    &:active:focus {
      @apply tw-bg-pink-150 tw-outline-none;
    }
  }

  &--tertiary {
    @apply tw-border-0 tw-bg-transparent tw-text-secondary-base;

    &:hover {
      @apply tw-bg-secondary-light-x;
    }
  }

  &--link {
    @apply tw-whitespace-nowrap tw-rounded-full tw-border-transparent tw-bg-transparent tw-p-2 tw-text-secondary-base;

    &:hover,
    &:focus,
    &:active,
    &:active:focus {
      @apply tw-text-black;
    }
  }

  &--destructive {
    @apply tw-border tw-border-solid tw-border-transparent tw-bg-red-hex-800 tw-text-white;

    &:hover {
      @apply tw-bg-red-hex-900;
    }

    &:focus {
      @apply tw-bg-red-900;
    }

    &:active,
    &:active:focus {
      @apply tw-bg-red-900;
    }
  }

  &--suspended {
    @apply tw-absolute tw-border tw-border-solid tw-border-transparent tw-bg-white tw-text-secondary-base tw-shadow;

    z-index: 2;

    &:hover {
      @apply tw-bg-background-light;
    }

    &:focus {
      @apply tw-bg-gray-medium tw-outline-none;
    }

    &:active,
    &:active:focus {
      @apply tw-bg-gray-medium tw-outline-none;
    }
  }

  &--notification {
    @apply tw-whitespace-nowrap tw-rounded-full tw-border-transparent tw-bg-transparent tw-p-2 tw-text-secondary-base;

    &:hover,
    &:focus,
    &:active,
    &:active:focus {
      @apply tw-text-black;
    }
  }

  &--error {
    @apply tw-rounded-full tw-border tw-border-solid tw-border-transparent tw-bg-error-dark tw-text-white;

    &:hover {
      @apply tw-bg-error-dark;

      background-image: none;
      box-shadow:
        0 2px 4px rgb(0 0 0 / 14%),
        0 1px 5px rgb(0 0 0 / 20%);
    }

    &:active,
    &:focus,
    &:active:focus {
      @apply tw-bg-pink tw-outline-none;

      background-image: none;
    }
  }
}
</style>
